import { z } from 'zod';
export const OcppVersionSchema = z.enum(['1.6', '2.0.1', '2.1']);
export const ConnectorTypeSchema = z.enum(['J1772', 'NACS', 'CCS1']);
export const ConnectorStatusSchema = z.enum([
    'Available', 'Occupied', 'Faulted', 'Unavailable', 'Reserved',
    'Charging', 'Finishing', 'Preparing', 'SuspendedEV', 'SuspendedEVSE',
]);
export const ConnectorSchema = z.object({
    connector_id: z.number().int().positive(),
    type: ConnectorTypeSchema,
    max_power_kw: z.number().positive(),
    status: ConnectorStatusSchema,
    status_updated_at: z.coerce.date(),
});
export const ChargerDiagnosticsSchema = z.object({
    temperature: z.number().optional(),
    voltage: z.number().optional(),
    current: z.number().optional(),
    power_factor: z.number().optional(),
    last_error_code: z.string().optional(),
    last_error_timestamp: z.coerce.date().optional(),
});
// Third-party (guest) access — sub-schemas.
// `days_of_week` is plural here; the pre-existing TOU schedule on
// Tariff uses `day_of_week`. Future cleanup unifies them into a
// shared timeWindow shape; foundation keeps them parallel.
export const GuestTimeWindowSchema = z.object({
    start_hour: z.number().int().min(0).max(23),
    end_hour: z.number().int().min(1).max(24),
    days_of_week: z.array(z.number().int().min(0).max(6)),
});
export const ChargerSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    location_id: z.string(),
    identity: z.string().min(1),
    vendor: z.string().min(1),
    model: z.string().min(1),
    serial_number: z.string().optional(),
    firmware_version: z.string().optional(),
    ocpp_version: OcppVersionSchema,
    connectors: z.array(ConnectorSchema),
    tariff_id: z.string().optional(),
    last_heartbeat: z.coerce.date().optional(),
    last_boot: z.coerce.date().optional(),
    is_online: z.boolean(),
    is_active: z.boolean(),
    configuration: z.record(z.string(), z.string()),
    diagnostics: ChargerDiagnosticsSchema,
    // Third-party (guest) access. All fields default to a tenant-only,
    // private posture. Optional in the parser so legacy serializations
    // round-trip cleanly; defaulted via Mongoose at the storage layer.
    is_open_to_guests: z.boolean().default(false),
    guest_pass_list: z.array(z.string()).max(100).default([]),
    guest_time_windows: z.array(GuestTimeWindowSchema).default([]),
    guest_markup_bps: z.number().int().min(0).max(50_000).nullable().default(null),
    public_network: z.boolean().default(true),
    // ── Resident-managed chargers (peer-to-peer sharing) ──────────────
    /** ObjectId of the driver who registered this charger via the PWA
     *  "My Chargers" flow. Null on operator-created chargers. */
    created_by_user_id: z.string().nullable().optional(),
    /** Provenance tag — distinguishes admin-created from resident-self-
     *  service. Defaults to 'admin' on legacy docs missing the field. */
    created_via: z.enum(['admin', 'resident_self_service']).default('admin'),
    /** Per-pass-list-member free-text notes (Map<userIdString, note>).
     *  Optional in the parser since not every charger has any. */
    contact_notes: z.record(z.string(), z.string()).optional(),
    /** Optional photo URL for the charger (resident-set or admin-set).
     *  Null falls back to a gradient placeholder PWA-side. */
    photo_url: z.string().nullable().optional(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
export const RemoteCommandSchema = z.enum([
    'start', 'stop', 'reset_soft', 'reset_hard', 'unlock', 'update_firmware', 'get_diagnostics',
]);
export const RegisterChargerSchema = z.object({
    location_id: z.string(),
    identity: z.string().min(1).max(20),
    vendor: z.string().min(1),
    model: z.string().min(1),
    ocpp_version: OcppVersionSchema.default('1.6'),
    tariff_id: z.string().optional(),
    connectors: z.array(z.object({
        connector_id: z.number().int().positive(),
        type: ConnectorTypeSchema,
        max_power_kw: z.number().positive(),
    })).min(1),
});
//# sourceMappingURL=charger.js.map